import React, {
  useState,
  Fragment,
  useMemo,
  useEffect,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { useHistory, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { graphql } from 'react-apollo';

import { mapProps } from '../../../../../../../util/hocs';
import { MATCHED_PHOTOS_SORT } from '../../../../../../../constants/photos';
import { createLoadMoreMethod } from '../../../../../../../util/apollo';
import IdentityNullState from '../../../../../../pro/views/HierarchyItem/Matches/components/IdentityNullState';
import Placeholder from '../../../../../../pro/views/HierarchyItem/Matches/components/Placeholder';
import PhotoGrid from '../../../../../../../components/grid/PhotoGrid';
import useScrollToTop from '../../../../../../../hooks/useScrollToTop';
import {
  hasNonEntitledPhotos,
  isMatchedPhotoPpaAvailable,
  isPhotoEntitledById,
  membershipsCartContainsPhoto,
} from '../../../../../../pro/views/HierarchyItem/Matches/helpers';
import MatchedPhoto from '../../../../../../pro/views/HierarchyItem/Matches/components/MatchedPhoto';
import getAlbumMembershipGroupIdentity from '../../graphql/queries/getAlbumMembershipGroupIdentity';
import getAlbumMembershipGroup from '../../graphql/queries/getSimplifiedAlbumMembershipGroup';
import getAlbumMembershipGroupPurchases from '../../graphql/queries/getAlbumMembershipGroupPurchases';
import ObfuscationInstructionOverlayGuard from '../ObfuscationInstructionOverlayGuard';
import {
  openModal as openModalAction,
  closeModal as closeModalAction,
} from '../../../../../../../actions/modal-actions';
import { REPORT_PHOTO_MODAL } from '../../../../../../../constants/modals';
import {
  albumMembershipGroupAllowsPublicPhotoAttribution,
  albumMembershipGroupHasSocialShareEnabled,
  getAlbumMembershipGroupEntityAccount,
  getAlbumMembershipGroupHashcode,
  isPictureDayMembership,
  isYearbookMembership,
} from '../../helpers';
import { canReportPhotos } from '../../../../../../../util/product-permissions';
import { PurchasingOptionsModal } from '../../../../../../pro/views/HierarchyItem/Matches/components/PurchasingOptions';
import useBodyClass from '../../../../../../../hooks/useBodyClass';
import useAlbumMembershipGroupPurchasingOptionsCheck from '../../../../../../pro/views/HierarchyItem/Matches/hooks/useAlbumMembershipGroupPurchasingOptionsCheck';
import useAlbumMembershipGroupPrintBackgrounds from '../../../../../../pro/views/HierarchyItem/Matches/hooks/useAlbumMembershipGroupPrintBackgrounds';
import MatchedPhotoActions from '../../../../../../pro/views/HierarchyItem/Matches/components/MatchedPhoto/MatchedPhotoActions';
import MatchedPhotoPhotoViewerActions from '../../../../../../pro/views/HierarchyItem/Matches/components/MatchedPhoto/MatchedPhotoPhotoViewerActions';
import PhotoGridViewerFooter from '../../../../../../pro/views/Album/AlbumPhotos/components/PhotoGridViewerFooter';
import MatchesHeadMeta from '../../../../../../pro/views/HierarchyItem/Matches/components/MatchesHeadMeta';
import usePurchasesPendingConfiguration from '../../../../../../pro/views/HierarchyItem/Matches/components/PurchasesPendingConfiguration/hooks/usePurchasesPendingConfiguration';
import { PURCHASES_PENDING_CONFIGURATION_MODAL } from '../../../../../../pro/views/HierarchyItem/Matches/components/PurchasesPendingConfiguration';
import GlobalWaldogramConfiguratorModal, {
  GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL,
} from '../../../../../../pro/views/AccountAddressBook/components/GlobalWaldogramConfigurator/modal';
import NoUploadsNullState from '../../../../../../pro/views/HierarchyItem/Matches/components/NoUploadsNullState/NoUploadsNullState';
import ImagePreload from '../../../../../../../components/global/ImagePreload';
import DigitalBundlesMissingConfigurationNotice from '../../../../../../pro/views/HierarchyItem/Matches/components/DigitalBundlesMissingConfigurationNotice';
import useIdentityJerseyColors from '../../../../../../pro/views/HierarchyItem/Matches/hooks/useIdentityJerseyColors';
import JerseyColorSelectorModal, {
  JERSEY_COLOR_SELECTOR_MODAL,
} from '../../../../../../pro/views/HierarchyItem/Matches/components/JerseyColorSelector';
import { extractWatermarkSettings } from '../../../../../../../util/watermark-settings';
import { HierarchyItemForMatches } from '../../../../../../pro/views/HierarchyItem/Matches/graphql/queries/hierarchy-item';
import Button from '../../../../../../../components/global/Button';
import CharityNotice from '../../../../../../pro/views/HierarchyItem/Matches/components/CharityNotice';
import useAccount from '../../../../../../../components/global/hooks/useAccount';
import { MATCHED_PHOTO_WATERMARK_MODE } from '../../../../../../../constants/pro-settings';
import MatchedPhotosSortFilter from '../../../../../../pro/views/HierarchyItem/Matches/components/MatchedPhotosSortFilter';
import PhotoSelectorHeader from '../../../../../../pro/views/HierarchyItem/components/PhotoSelectorHeader';
import MatchedPhotoSelectionAction from '../../../../../../pro/views/HierarchyItem/Matches/components/MatchedPhoto/MatchedPhotoSelectionAction';
import { PhotoSelectorContext } from '../../../../../../../components/global/PhotoSelectorContext';
import { PHOTO_DOWNLOAD_MODAL } from '../../../../../../../components/global/PhotoDownload';
import PhotoDownloadModal from '../../../../../../../components/global/PhotoDownload/modal';
import ShiftSelect from '../../../../../../../components/global/ShiftSelect';
import PhotoSelectorToolbar from '../../../../../../pro/views/HierarchyItem/components/PhotoSelectorToolbar';

const propTypes = {
  albumMembershipGroupIdentity: PropTypes.shape({
    identity: PropTypes.shape({
      id: PropTypes.string,
    }),
    matched_photos_extended: PropTypes.shape({
      total: PropTypes.number,
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          photo: PropTypes.shape({
            id: PropTypes.string,
            thumbnail_urls: PropTypes.array,
          }),
          album: PropTypes.shape({
            id: PropTypes.string.isRequired,
          }).isRequired,
        }),
      ),
    }),
    entitled_matched_photos: PropTypes.shape({
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
    }),
  }),
  albumMembershipGroupFilters: PropTypes.array,
  albumMembershipGroup: PropTypes.shape({
    album: PropTypes.shape({
      id: PropTypes.string.isRequired,
      print_packages: PropTypes.shape({
        total: PropTypes.number,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
          }),
        ),
      }),
    }),
  }),
  cart: PropTypes.array.isRequired,
  loadMore: PropTypes.func,
  loading: PropTypes.bool,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  matchedPhotoPrintBackgrounds: PropTypes.object,
  membershipEntitledPurchases: PropTypes.arrayOf(PropTypes.string),
  activeIdentity: PropTypes.shape({
    id: PropTypes.string,
  }),
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
};

const MatchedPhotos = ({
  albumMembershipGroupIdentity,
  albumMembershipGroupFilters,
  loadMore,
  loading,
  albumMembershipGroup,
  cart,
  openModal,
  closeModal,
  matchedPhotoPrintBackgrounds,
  membershipEntitledPurchases,
  activeIdentity,
  album,
  albumFolder,
}) => {
  useScrollToTop([albumMembershipGroupIdentity?.identity.id]);
  const { account } = useAccount();
  const entitledPurchases = membershipEntitledPurchases ?? [];
  const history = useHistory();
  const [selectedMatchedPhoto, setSelectedMatchedPhoto] = useState(null);

  const isPictureDayEvent = !!(
    albumFolder?.picture_day_enabled ??
    album?.root_album_folder?.picture_day_enabled
  );

  const isGreenScreenEnabled = !!(
    albumFolder?.green_screen_enabled ??
    album?.root_album_folder?.green_screen_enabled
  );

  const [matchedPhotosSort, setMatchedPhotosSort] = useState(
    isPictureDayEvent
      ? MATCHED_PHOTOS_SORT.BEST_SCORE
      : MATCHED_PHOTOS_SORT.MOST_RECENT,
  );

  const {
    selectedPhotos,
    selectionSettings,
    selectionEnabled,
    onSelectPhotos,
    onDeselectPhotos,
    onResetSelection,
  } = useContext(PhotoSelectorContext);
  const { isSelectAll } = selectionSettings;

  useEffect(() => {
    onResetSelection?.();
  }, [activeIdentity?.id]);

  const isMobile = useMediaQuery({ maxWidth: 767 });
  useBodyClass('purchasing-options-modal-open', !!selectedMatchedPhoto);
  const {
    hasPurchasedPrintPackages,
    prepaidPrintPackagesMissingConfiguration,
    prepaidPhotoPricingsMissingConfiguration,
    prepaidCustomPrintsMissingConfiguration,
  } = usePurchasesPendingConfiguration({
    hierarchyItemId: albumMembershipGroup?.id,
  });

  const itemsMissingConfigurationTotal =
    (prepaidPrintPackagesMissingConfiguration?.length ?? 0) +
    (prepaidPhotoPricingsMissingConfiguration?.length ?? 0) +
    (prepaidCustomPrintsMissingConfiguration?.length ?? 0);

  const albumFolderId = albumMembershipGroup?.is_album
    ? albumMembershipGroup?.album?.root_album_folder?.id
    : albumMembershipGroup?.album_folder?.id;

  const vendorAccount =
    getAlbumMembershipGroupEntityAccount(albumMembershipGroup);

  const { sell_prints_enabled: sellPrintsEnabled = true } =
    vendorAccount?.pro_settings ?? {};

  const { jerseyColors, loadingJerseyColors } = useIdentityJerseyColors({
    identityId: albumMembershipGroupIdentity?.identity?.id,
    albumFolderId,
    isGreenScreenEnabled,
  });
  const { hasPurchasingOptions, loading: loadingPurchasingOptionsCheck } =
    useAlbumMembershipGroupPurchasingOptionsCheck({
      albumMembershipGroupId: albumMembershipGroup?.id,
      identity: albumMembershipGroupIdentity?.identity,
      sellPrintsEnabled,
    });

  const { printBackgrounds, loading: loadingPrintBackgrounds } =
    useAlbumMembershipGroupPrintBackgrounds({
      albumMembershipGroupId: albumMembershipGroup?.id,
      jerseyColors,
      isGreenScreenEnabled,
    });

  const printBackgroundUrls = useMemo(
    () => printBackgrounds.map(background => background.asset_image.url),
    [printBackgrounds],
  );

  const isPictureDay = isPictureDayMembership(albumMembershipGroup);
  const isYearbookEvent = isYearbookMembership(albumMembershipGroup);

  const watermarkSettings = useMemo(
    () => extractWatermarkSettings(vendorAccount),
    [vendorAccount],
  );

  // TODO @consumer: We'd like to have matched photos here sorted by
  // the largest facebox (DESC) but it has to be server side because
  // we also need to support consistent pagination.
  const matchedPhotos = albumMembershipGroupIdentity?.matched_photos_extended;
  const entitledMatchedPhotos = useMemo(
    () =>
      (matchedPhotos?.records ?? []).filter(({ photo }) =>
        isPhotoEntitledById(albumMembershipGroupIdentity, photo.id),
      ),
    [albumMembershipGroupIdentity, matchedPhotos],
  );
  const totalPhotos = entitledMatchedPhotos?.length;
  const totalSelectedPhotos = useMemo(() => {
    return isSelectAll
      ? albumMembershipGroupIdentity?.entitled_matched_photos?.total -
          selectedPhotos.length
      : selectedPhotos.length;
  }, [isSelectAll, entitledMatchedPhotos, selectedPhotos]);
  const socialShareEnabled =
    albumMembershipGroupHasSocialShareEnabled(albumMembershipGroup);
  const hashCode = getAlbumMembershipGroupHashcode(albumMembershipGroup);

  const yearbookMatchedPhoto = useMemo(
    () =>
      (matchedPhotos?.records ?? []).find(
        ({ yearbook_photo: yearbookPhoto }) => !!yearbookPhoto,
      ),
    [matchedPhotos?.records],
  );

  useEffect(() => {
    if (
      !loadingJerseyColors &&
      jerseyColors.length === 0 &&
      !!albumMembershipGroupIdentity?.identity?.id &&
      (printBackgrounds ?? []).some(
        background => background.jersey_colors?.records?.length > 0,
      )
    ) {
      openModal(JERSEY_COLOR_SELECTOR_MODAL, {
        identityId: albumMembershipGroupIdentity.identity.id,
        albumFolderId,
      });
    }
  }, [
    albumMembershipGroupIdentity,
    albumMembershipGroup,
    loadingJerseyColors,
    jerseyColors,
  ]);

  if (
    !albumMembershipGroupIdentity ||
    loading ||
    loadingJerseyColors ||
    loadingPurchasingOptionsCheck ||
    loadingPrintBackgrounds
  ) {
    return <Placeholder />;
  }

  // When there are pictures and the user is obfuscated in the first.
  const hasObfuscation =
    matchedPhotos.total > 0 &&
    watermarkSettings?.watermarkMode !==
      MATCHED_PHOTO_WATERMARK_MODE.WALLPAPER &&
    !isPhotoEntitledById(
      albumMembershipGroupIdentity,
      matchedPhotos.records[0].photo.id,
    );

  // A scenario where the user is not allowed access to album photos & has matches
  // can exist, so add a safeguard.
  if (
    albumMembershipGroup?.album?.photos?.total === 0 &&
    matchedPhotos?.total === 0
  ) {
    return (
      <Fragment>
        <MatchesHeadMeta albumMembershipGroup={albumMembershipGroup} />
        <NoUploadsNullState identity={albumMembershipGroupIdentity?.identity} />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <MatchesHeadMeta albumMembershipGroup={albumMembershipGroup} />
      <Fragment>
        {matchedPhotos.records.length > 0 && (
          <div className="matched-photos-filter">
            <div
              className={`d-flex w-100${selectionEnabled ? '' : ' px-2 pt-2'}`}
            >
              {!selectionEnabled && (
                <MatchedPhotosSortFilter
                  selectedFilter={matchedPhotosSort}
                  setSelectedFilter={setMatchedPhotosSort}
                  className="ml-0"
                />
              )}
              <div className="d-flex justify-content-end ml-auto w-100 max-w-50p">
                {isGreenScreenEnabled && entitledMatchedPhotos?.length > 0 && (
                  <Button
                    btnSize="sm"
                    btnStyle="link"
                    className="text-extra-bold mb-2"
                    onClick={() =>
                      history.push(
                        `/cards/downloads-configurator/${
                          (albumMembershipGroup ?? album)?.id
                        }`,
                      )
                    }
                  >
                    Configure downloads
                  </Button>
                )}
                {!isGreenScreenEnabled && (
                  <PhotoSelectorHeader
                    totalPhotos={totalPhotos}
                    className="w-auto"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </Fragment>
      <div className="matched-photos-wrap">
        {!matchedPhotos.records.length && (
          <IdentityNullState
            identity={albumMembershipGroupIdentity.identity}
            hasFilters={(albumMembershipGroupFilters ?? []).length > 0}
          />
        )}

        {hasObfuscation && <ObfuscationInstructionOverlayGuard />}

        <ShiftSelect
          items={(matchedPhotos?.records ?? [])?.map(({ photo }) => photo.id)}
          onSelectItems={onSelectPhotos}
          onDeselectItems={onDeselectPhotos}
        >
          {({ onToggleSelectedItem: onToggleSelectedPhoto }) => (
            <PhotoGrid
              key={`${activeIdentity?.id}`}
              records={matchedPhotos.records}
              loadMore={loadMore}
              total={matchedPhotos.total}
              targetRowHeight={isMobile ? 300 : 380}
              recordPhotoAccessor="photo"
              justifiedLayoutOptions={{
                targetRowHeightTolerance: isMobile ? 0.5 : 0.175,
                widowLayoutStyle: isMobile ? 'justify' : 'left',
              }}
              nativePhotoViewer
              photoViewerProps={{
                renderPhoto: ({ record: matchedPhoto }) => {
                  const isEntitled = isPhotoEntitledById(
                    albumMembershipGroupIdentity,
                    matchedPhoto.photo.id,
                  );

                  return (
                    <MatchedPhoto
                      withWatermark
                      key={matchedPhoto.id}
                      printBackgrounds={printBackgrounds}
                      matchedPhoto={matchedPhoto}
                      isEntitled={isEntitled}
                      isPictureDay={isPictureDay}
                      isYearbookEvent={isYearbookEvent}
                      existingYearbookMatchedPhotoId={yearbookMatchedPhoto?.id}
                      albumFolderId={albumFolderId}
                      identityId={albumMembershipGroupIdentity?.identity?.id}
                      displayAttribution={albumMembershipGroupAllowsPublicPhotoAttribution(
                        albumMembershipGroup,
                      )}
                      watermarkSettings={watermarkSettings}
                      account={account}
                      vendorAccount={vendorAccount}
                    />
                  );
                },
                actions: ({ record: matchedPhoto }) => {
                  const isEntitled = isPhotoEntitledById(
                    albumMembershipGroupIdentity,
                    matchedPhoto.photo.id,
                  );

                  const isPpaAvailable =
                    isMatchedPhotoPpaAvailable(matchedPhoto);

                  return (
                    <MatchedPhotoPhotoViewerActions
                      matchedPhoto={matchedPhoto}
                      isEntitled={isEntitled}
                      onPhotoItemPurchase={() =>
                        setSelectedMatchedPhoto(matchedPhoto)
                      }
                      // Entitled photos can always be purchased as premium or legacy prints
                      canPurchasePhotoItems={
                        hasPurchasingOptions ||
                        (sellPrintsEnabled && (isEntitled || isPpaAvailable))
                      }
                      canReportPhoto={canReportPhotos(matchedPhoto.album)}
                      hasCartItems={membershipsCartContainsPhoto(
                        cart,
                        matchedPhoto.photo.id,
                      )}
                      onPhotoReport={() =>
                        openModal(REPORT_PHOTO_MODAL, {
                          photoId: matchedPhoto.photo.id,
                          albumId: matchedPhoto.album?.id,
                        })
                      }
                      socialShareEnabled={socialShareEnabled}
                      hashCode={hashCode}
                      openModal={openModal}
                      onPhotoItemWaldogramPurchase={
                        !sellPrintsEnabled
                          ? undefined
                          : () => {
                              openModal(GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL, {
                                photoId: matchedPhoto.photo.id,
                              });
                            }
                      }
                      onPhotoItemCampergramPurchase={
                        matchedPhoto?.album?.custom_prints_meta
                          ?.onsite_delivery_enabled
                          ? () =>
                              history.push(
                                `/app/shutterbug/hierarchy-items/${matchedPhoto.album.id}/postcard?photoId=${matchedPhoto.photo.id}`,
                              )
                          : undefined
                      }
                      albumFolderId={albumFolderId}
                      sellPhotosModeEnabled={isPictureDay}
                    />
                  );
                },
                footer: ({ record: matchedPhoto }) => {
                  return (
                    <PhotoGridViewerFooter
                      entity={
                        albumMembershipGroup?.album ??
                        albumMembershipGroup?.album_folder
                      }
                      photo={matchedPhoto?.photo}
                    />
                  );
                },
              }}
              cellRenderer={({ record: matchedPhoto, recordPath }) => {
                const { photo } = matchedPhoto;
                const isEntitled = isPhotoEntitledById(
                  albumMembershipGroupIdentity,
                  photo.id,
                );

                const albumMembershipPhoto = {
                  ...matchedPhoto,
                };

                const isPpaAvailable = isMatchedPhotoPpaAvailable(matchedPhoto);

                return (
                  <MatchedPhoto
                    withWatermark
                    key={matchedPhoto.id}
                    printBackgrounds={printBackgrounds}
                    matchedPhoto={matchedPhoto}
                    isEntitled={isEntitled}
                    isPictureDay={isPictureDay}
                    isYearbookEvent={isYearbookEvent}
                    existingYearbookMatchedPhotoId={yearbookMatchedPhoto?.id}
                    albumFolderId={albumFolderId}
                    identityId={albumMembershipGroupIdentity?.identity?.id}
                    linkTo={recordPath}
                    actions={(() => {
                      if (selectionEnabled) {
                        return (
                          <MatchedPhotoSelectionAction
                            matchedPhoto={albumMembershipPhoto}
                            selectable={isEntitled}
                            isSelected={
                              isSelectAll
                                ? !selectedPhotos.includes(photo.id)
                                : selectedPhotos.includes(photo.id)
                            }
                            onSelectToggle={() =>
                              onToggleSelectedPhoto(
                                photo.id,
                                selectedPhotos.includes(photo.id),
                              )
                            }
                          />
                        );
                      }

                      return (
                        <MatchedPhotoActions
                          matchedPhoto={matchedPhoto}
                          isEntitled={isEntitled}
                          onPhotoItemPurchase={() =>
                            setSelectedMatchedPhoto(matchedPhoto)
                          }
                          hasUnconfiguredPrePurchases={
                            itemsMissingConfigurationTotal > 0
                          }
                          onConfigurePrepaidItems={() =>
                            openModal(PURCHASES_PENDING_CONFIGURATION_MODAL, {
                              albumMembershipPhoto: {
                                ...matchedPhoto,
                                print_background: printBackgrounds?.find(
                                  background =>
                                    background.id ===
                                    matchedPhotoPrintBackgrounds?.[
                                      matchedPhoto?.id
                                    ],
                                ),
                              },
                              identityId: activeIdentity?.id,
                              watermarkSettings,
                            })
                          }
                          canPurchasePhotoItems={
                            hasPurchasingOptions ||
                            (sellPrintsEnabled &&
                              (isEntitled || isPpaAvailable))
                          }
                          canReportPhoto={canReportPhotos(matchedPhoto.album)}
                          hasCartItems={membershipsCartContainsPhoto(
                            cart,
                            matchedPhoto.photo.id,
                          )}
                          onPhotoReport={() =>
                            openModal(REPORT_PHOTO_MODAL, {
                              photoId: matchedPhoto.photo.id,
                              albumId: matchedPhoto.album?.id,
                            })
                          }
                          socialShareEnabled={socialShareEnabled}
                          hashCode={hashCode}
                          openModal={openModal}
                          linkTo={recordPath}
                          onPhotoItemWaldogramPurchase={
                            !sellPrintsEnabled
                              ? undefined
                              : () => {
                                  openModal(
                                    GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL,
                                    {
                                      photoId: matchedPhoto.photo.id,
                                    },
                                  );
                                }
                          }
                          onPhotoItemCampergramPurchase={
                            matchedPhoto?.album?.custom_prints_meta
                              ?.onsite_delivery_enabled
                              ? () =>
                                  history.push(
                                    `/app/shutterbug/hierarchy-items/${matchedPhoto.album.id}/postcard?photoId=${matchedPhoto.photo.id}`,
                                  )
                              : undefined
                          }
                          sellPhotosModeEnabled={isPictureDay}
                        />
                      );
                    })()}
                    displayAttribution={albumMembershipGroupAllowsPublicPhotoAttribution(
                      albumMembershipGroup,
                    )}
                    watermarkSettings={watermarkSettings}
                    account={account}
                    vendorAccount={vendorAccount}
                  />
                );
              }}
            />
          )}
        </ShiftSelect>
        <PhotoSelectorToolbar totalSelectedPhotos={totalSelectedPhotos} />
        {selectedMatchedPhoto && (
          <PurchasingOptionsModal
            albumFolderId={albumFolderId}
            hierarchyItemId={
              isMatchedPhotoPpaAvailable(selectedMatchedPhoto)
                ? (selectedMatchedPhoto?.album?.id ?? albumMembershipGroup?.id)
                : albumMembershipGroup?.id
            }
            hasPurchasedPrintPackages={
              albumMembershipGroupIdentity?.purchased_print_packages?.total >
                0 || hasPurchasedPrintPackages
            }
            forceOpen={!!selectedMatchedPhoto}
            photo={{
              ...selectedMatchedPhoto.photo,
              is_picture_day_enabled: isPictureDay,
              price_album_folder_tag:
                selectedMatchedPhoto.price_album_folder_tag,
              faceblocked_thumbnail_urls:
                selectedMatchedPhoto.faceblocked_thumbnail_urls,
              print_background: printBackgrounds?.find(
                background =>
                  background.id ===
                  matchedPhotoPrintBackgrounds?.[selectedMatchedPhoto?.id],
              ),
            }}
            album={selectedMatchedPhoto.album}
            onClose={() => setSelectedMatchedPhoto(null)}
            isPhotoEntitled={isPhotoEntitledById(
              albumMembershipGroupIdentity,
              selectedMatchedPhoto.photo.id,
            )}
            entitledPurchases={entitledPurchases}
          />
        )}
        <DigitalBundlesMissingConfigurationNotice
          albumMembershipGroup={albumMembershipGroup}
          matchedPhotosTotal={matchedPhotos?.total}
          identity={activeIdentity}
          className={
            hasNonEntitledPhotos(albumMembershipGroupIdentity) ? '' : 'd-none'
          }
        />
        <CharityNotice albumFolder={albumFolder} />
      </div>
      <GlobalWaldogramConfiguratorModal
        onDismiss={() => {
          closeModal(GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL);
        }}
      />
      <ImagePreload urls={printBackgroundUrls} />
      <JerseyColorSelectorModal />
      <PhotoDownloadModal
        totalPhotos={totalSelectedPhotos}
        photoDownloadCreateInput={{
          photo_ids: {
            select_all: isSelectAll,
            selected_ids: isSelectAll ? [] : selectedPhotos,
            ignored_ids: isSelectAll ? selectedPhotos : [],
          },
          album: album
            ? {
                album_id: album.id,
              }
            : undefined,
          album_folder: albumFolder
            ? {
                album_folder_id: albumFolder.id,
              }
            : undefined,
          matched_photos: {
            matched_photos_only: true,
            identity_ids: activeIdentity?.id ? [activeIdentity.id] : [],
          },
        }}
        onClose={() => {
          onResetSelection();
          closeModal(PHOTO_DOWNLOAD_MODAL);
        }}
      />
    </Fragment>
  );
};

MatchedPhotos.propTypes = propTypes;

export { MatchedPhotos as MatchedPhotosComponent };

const mapStateToProps = state => ({
  activeIdentity: state.checkout.activeIdentity,
  cart: state.checkout.cart,
  matchedPhotoPrintBackgrounds: state.matchedPhotos.printBackgrounds,
  albumMembershipGroupFilters:
    state.consumer.memberships.albumMembershipGroupFilters,
});

const mapDispatchToProps = {
  openModal: openModalAction,
  closeModal: closeModalAction,
};

const withHierarchyItem = graphql(HierarchyItemForMatches, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumMembershipGroupId,
    },
    fetchPolicy: 'cache-first',
  }),
  props: ({ data: { hierarchyItem } }) => ({
    album: hierarchyItem?.album,
    albumFolder: hierarchyItem?.album_folder,
  }),
  skip: ({ match }) => !match.params.albumMembershipGroupId,
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withHierarchyItem,
  mapProps(({ data, activeIdentity, album, albumFolder, ...props }) => ({
    ...props,
    album,
    albumFolder,
    activeIdentity,
    albumMembershipGroupId: props.match.params.albumMembershipGroupId,
    identityId: activeIdentity?.id,
    matchedPhotosSort:
      (albumFolder?.picture_day_enabled ??
      album?.root_album_folder?.picture_day_enabled)
        ? MATCHED_PHOTOS_SORT.BEST_SCORE
        : MATCHED_PHOTOS_SORT.MOST_RECENT,
  })),
  getAlbumMembershipGroupIdentity,
  mapProps(({ data, ...props }) => ({
    ...props,
    albumMembershipGroupIdentity: data?.albumMembershipGroup?.identities?.[0],
    loading: data?.loading,
    loadMore: createLoadMoreMethod({
      source: data?.albumMembershipGroup,
      recordsPath: 'identities[0].matched_photos_extended',
      fetchMore: data?.fetchMore,
    }),
  })),
  getAlbumMembershipGroup,
  mapProps(({ data, ...props }) => ({
    ...props,
    albumMembershipGroup: data?.albumMembershipGroup,
  })),
  getAlbumMembershipGroupPurchases,
  mapProps(({ data, ...props }) => ({
    ...props,
    membershipEntitledPurchases: data?.albumMembershipGroup
      ? [
          ...(data?.albumMembershipGroup?.purchased_photos?.records ?? []).map(
            photo => photo.id,
          ),
          ...(
            data?.albumMembershipGroup?.purchased_photo_downloads?.records ?? []
          ).map(record => record.photo.id),
          ...(
            data?.albumMembershipGroup?.entitled_matched_photos?.records ?? []
          ).map(record => record.id),
        ]
      : undefined,
  })),
)(MatchedPhotos);
